import React from 'react';
import { useTranslation } from 'react-i18next';
import { trackEvent, EVENTS } from '../../lib/analytics';

class ErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false, error: null, errorInfo: null };
  }

  static getDerivedStateFromError(error) {
    // Update state so the next render will show the fallback UI
    return { hasError: true };
  }

  componentDidCatch(error, errorInfo) {
    // Log error details
    console.error('ErrorBoundary caught an error:', error, errorInfo);
    
    // Track error in analytics
    trackEvent(EVENTS.ERROR_OCCURRED, {
      errorType: 'react_error_boundary',
      errorMessage: error.message,
      errorStack: error.stack,
      componentStack: errorInfo.componentStack
    });
    
    this.setState({
      error: error,
      errorInfo: errorInfo
    });
  }

  render() {
    if (this.state.hasError) {
      return <ErrorFallback error={this.state.error} />;
    }

    return this.props.children;
  }
}

function ErrorFallback({ error }) {
  const { t } = useTranslation();
  
  const handleReload = () => {
    window.location.reload();
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-gray-100 px-4">
      <div className="max-w-md w-full bg-white rounded-lg shadow-md p-8 text-center">
        <div className="text-6xl mb-6">⚠️</div>
        <h2 className="text-2xl font-bold mb-4 text-red-600">
          {t('error.something_went_wrong')}
        </h2>
        <p className="text-gray-600 mb-6">
          {t('error.try_reloading')}
        </p>
        {process.env.NODE_ENV === 'development' && error && (
          <details className="text-left mb-6 p-4 bg-gray-100 rounded text-sm">
            <summary className="cursor-pointer font-medium">
              {t('error.error_details')}
            </summary>
            <pre className="mt-2 whitespace-pre-wrap">
              {error.message}
              {error.stack}
            </pre>
          </details>
        )}
        <button
          onClick={handleReload}
          className="w-full bg-blue-600 text-white py-3 rounded-md hover:bg-blue-700"
        >
          {t('error.reload_page')}
        </button>
      </div>
    </div>
  );
}

export default ErrorBoundary;
